import { useState, useEffect, useCallback } from "react";
import { Formik, Form, FieldArray, useFormikContext } from "formik";
import { UnlockBenefits } from "../../Components/AddStudent/UnlockBenefits";
import { KidDetailsForm } from "../../Components/AddStudent/KidDetailsForm";
import { AssignProgress } from "../../Components/AddStudent/AssignProgress";
import { AddKidButton } from "../../Components/AddStudent/ActionButtons";
import { OnboardingPaymentButtons } from "../../Components/AddStudent/OnboardingPaymentButtons";
import { AddStudentSkeleton } from "../../Components/AddStudent/AddStudentSkeleton";
import { useCompleteOnboarding } from "../../../../hooks/authV2/useCompleteOnboarding";
import { useGetOnboardingStudents } from "../../../../hooks/authV2/useGetOnboardingStudents";
import { useDeleteKid } from "../../../../hooks/authV2/useDeleteKid";
import { useGetPlan } from "../../../../hooks/authV2/useGetPlan";
import { useSwitchToParent } from "../../../../hooks/authV2/useSwitchToParent";
import "./AddStudentPage.css";
import { Navbar } from "../../../WebUi/Components/HomeV2/Navbar";
import toast from "react-hot-toast";
import { INITIAL_KID_STATE, validationSchema } from "./ValidationSchema.js";
import { mapStudentToKid, getBillingPeriodLabel } from "./helper";
import { ERROR_MESSAGES } from "../../../../constant/constant.js";
import {
  saveStudentDraft,
  loadStudentDraft,
  clearStudentDraft,
  reconcileDraftWithServer,
  getParentId,
} from "../../../../utils/studentDraftStorage";

/**
 * Subcomponent to auto-save Formik draft kids debounced to localStorage.
 */
const FormikDraftAutoSave = ({ parentId }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    const pid = parentId || getParentId() || "guest";
    const timer = setTimeout(() => {
      saveStudentDraft(pid, values);
    }, 400);

    return () => clearTimeout(timer);
  }, [values, parentId]);

  return null;
};

const MAX_STUDENTS = 5;
/**
 * @param {object} props
 * @param {number} [props.already_registered_user=0] - Number of users already registered in a different flow, reduces available slots.
 */
export const AddStudentPage = ({ already_registered_user = 0 }) => {
  const {
    completeOnboarding,
    loading: isSubmittingApi,
    error,
    fieldErrors,
    setFieldErrors,
  } = useCompleteOnboarding();
  const {
    fetchOnboardingStudents,
    loading: isFetchingStudents,
    data: studentsData,
  } = useGetOnboardingStudents();
  const { deleteKid } = useDeleteKid();
  const {
    fetchPlans,
    loading: isFetchingPlans,
    data: plansData,
  } = useGetPlan();
  const { switchToParent } = useSwitchToParent();

  const [initialLoading, setInitialLoading] = useState(true);
  const [deletingKidId, setDeletingKidId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [initialKids, setInitialKids] = useState([{ ...INITIAL_KID_STATE }]);
  const [initialProgressAssignedTo, setInitialProgressAssignedTo] = useState(null);
  const [parentId, setParentId] = useState(() => getParentId());
  const [errorOnLoading, setErrorOnLoading] = useState(false);

  const availableSlots = MAX_STUDENTS - already_registered_user;

  // Fetch existing students and plan data on mount
  const loadInitialData = async () => {
    try {
      // Step 1: Switch to parent first to get the parent token
      await switchToParent();

      // Step 2: Read parent token from localStorage (set by switchToParent)
      const parentLocalData = JSON.parse(
        localStorage.getItem("parentLocalData"),
      );
      const parentToken = parentLocalData?.token;

      if (!parentToken) {
        throw new Error(ERROR_MESSAGES.FORBIDDEN);
      }

      // Step 3: Fetch students and plans in parallel using parent token
      await Promise.all([
        fetchOnboardingStudents(),
        fetchPlans({ token: parentToken }),
      ]);
    } catch (err) {
      setErrorOnLoading(true);
    } finally {
      setInitialLoading(false);
    }
  };
  useEffect(() => {
    loadInitialData();
  }, []);

  // Function to refetch the data when their is some error on loading inital data
  const refetchData = () => {
    setInitialLoading(true);
    setErrorOnLoading(false);
    loadInitialData();
  };

  // Reconcile server students with uncommitted drafts when initial data arrives
  useEffect(() => {
    if (initialLoading || isFetchingStudents) return;

    const currentParentId = getParentId();
    if (currentParentId) {
      setParentId(currentParentId);
    }

    const draftData = loadStudentDraft(currentParentId);
    const rawServerStudents = studentsData?.students || [];

    const {
      reconciledKids,
      overflowCount,
      duplicateCount,
      reconciledProgressAssignedTo,
    } = reconcileDraftWithServer(
      rawServerStudents,
      draftData,
      availableSlots,
    );

    if (overflowCount > 0) {
      toast(
        `To stay within your ${MAX_STUDENTS}-student limit, ${overflowCount} drafted student(s) were removed because students were added from another session.`,
        { icon: "⚠️", duration: 6000 },
      );
    } else if (duplicateCount > 0) {
      toast(
        `${duplicateCount} duplicate drafted student(s) were removed because they are already registered.`,
        { icon: "ℹ️", duration: 5000 },
      );
    }

    setInitialKids(reconciledKids);
    if (reconciledProgressAssignedTo != null) {
      setInitialProgressAssignedTo(reconciledProgressAssignedTo);
    }

    // If drafts were trimmed or deduplicated, immediately update storage to prevent repeated alerts
    if (overflowCount > 0 || duplicateCount > 0) {
      saveStudentDraft(currentParentId, {
        kids: reconciledKids,
        progressAssignedTo: reconciledProgressAssignedTo,
      });
    }
  }, [studentsData, initialLoading, isFetchingStudents, availableSlots]);

  // Extract the first plan from the fetched plans data
  const plan = plansData?.[0] || null;
  const tiers = plan?.tiers || [];
  const currency = plan?.currency || "INR";
  const currencySymbol = currency === "INR" ? "₹" : "$";
  const billingPeriod = getBillingPeriodLabel(plan?.billing_cycle);

  /**
   * Returns the price for a kid at a given index based on the plan tiers.
   * If the kid index exceeds available tiers, the last tier's price is used.
   */
  const getPriceForKid = useCallback(
    (index) => {
      if (!tiers.length) return 0;
      if (index < tiers.length) {
        return Number(tiers[index].price) || 0;
      }
      // Beyond available tiers → use the last tier's price
      return Number(tiers[tiers.length - 1].price) || 0;
    },
    [tiers],
  );

  /**
   * Returns the "next kid" price shown on the Add Kid button.
   * Uses the tier for the next index, capped at the last tier.
   */
  const getNextKidPrice = useCallback(
    (currentCount) => {
      if (!tiers.length) return 0;
      const nextIndex = currentCount;
      if (nextIndex < tiers.length) {
        return Number(tiers[nextIndex].price) || 0;
      }
      return Number(tiers[tiers.length - 1].price) || 0;
    },
    [tiers],
  );

  const handleRemoveKid = useCallback(
    async (kid, index, remove, values, setFieldValue) => {
      setDeleteError("");

      // Block deletion of students with transferred progress
      if (kid._status === "old" && kid.has_transferred_progress) {
        toast.error(
          "This student cannot be removed because progress has been transferred to them.",
        );
        return;
      }

      if (kid._status === "old" && kid._id) {
        setDeletingKidId(kid._id);
        try {
          const result = await deleteKid(kid._id);
          if (!result) {
            setDeleteError("Failed to remove student. Please try again.");
            toast.error("Failed to remove student. Please try again.");
            return;
          }
        } catch (err) {
          setDeleteError(err?.message || "Failed to remove student.");
          toast.error("Failed to remove student. Please try again.");
          return;
        } finally {
          setDeletingKidId(null);
        }
      }

      remove(index);

      const currentKidsCount = values.kids.length;
      if (values.progressAssignedTo >= currentKidsCount - 1) {
        setFieldValue("progressAssignedTo", Math.max(0, currentKidsCount - 2));
      } else if (values.progressAssignedTo === index) {
        setFieldValue("progressAssignedTo", 0);
      }
    },
    [deleteKid],
  );

  /**
   * Maps a student from the completeOnboarding response to Formik kid shape.
   * Preserves has_transferred_progress from existing state or the request.
   */
  const mapResponseStudentToKid = useCallback(
    (student, requestKids) => {
      const nameParts = (student.name || "").trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Check if this student already existed with transferred progress
      const existingKid = initialKids.find((k) => k._id === student.id);

      // Find the matching request kid to preserve transfer_progress info
      const matchingRequestKid = requestKids?.find(
        (k) => k?.username === student?.username || k?.email === student?.email,
      );

      return {
        _id: student.id,
        _status: "old",
        firstName,
        lastName,
        username: student.username || "",
        age: student.age ? String(student.age) : "",
        password: "",
        email: student.email || "",
        // Once transferred, stays true — even if the request sent false
        has_transferred_progress:
          existingKid?.has_transferred_progress ||
          matchingRequestKid?.transfer_progress ||
          false,
      };
    },
    [initialKids],
  );

  /**
   * Called by OnboardingPaymentButtons before initiating payment.
   * Builds the onboarding payload from Formik values, calls completeOnboarding,
   * and returns the result so the payment flow can continue.
   * @param {Object} values - Formik values
   * @returns {Promise<{students: Array}|null>} result on success, null on failure
   */
  const handleSaveKids = async (values) => {
    setFieldErrors(null);
    try {
      const onboardingData = {
        kids: values.kids.map((kid, index) => {
          const kidData = {
            firstName: kid.firstName,
            lastName: kid.lastName,
            age: kid.age,
            transfer_progress:
              values.progressAssignedTo != null &&
              values.progressAssignedTo === index,
          };

          // Only include username for kids aged 12 or younger
          if (kid.age <= 12) {
            kidData.username = kid.username;
          }

          // Pass id for existing (old) students to trigger update
          if (kid._status === "old" && kid._id) {
            kidData.id = kid._id;
            /*
              If we already transfer the data to the user we cannot again tranfer even to same user, so we are making
              this transfer_progress as false. Even after sending the false value it persists in the database.
              */
            if (kid.has_transferred_progress == true) {
              kidData.transfer_progress = false;
            }
          }

          if (kid.age > 12) {
            kidData.email = kid.email;
          } else {
            kidData.password = kid.password || "Aman@123";
          }
          return kidData;
        }),
      };

      const result = await completeOnboarding(onboardingData);

      if (result?.students) {
        // Clear draft from localStorage on success
        clearStudentDraft(parentId || getParentId());

        // Success: map returned students (with IDs) back to Formik shape
        const updatedKids = result.students.map((student) =>
          mapResponseStudentToKid(student, onboardingData.kids),
        );
        setInitialKids(updatedKids);
        return result;
      }

      return null;
    } catch (err) {
      console.log(err);
      return null;
    }
  };

  if (initialLoading || isFetchingStudents || isFetchingPlans) {
    return (
      <div className="addStudentPage">
        <Navbar />
        <main className="addStudentPage__main container-fluid">
          <div className="addStudentPage__content">
            <AddStudentSkeleton />
          </div>
        </main>
      </div>
    );
  }

  if (errorOnLoading) {
    return (
      <div className="addStudentPage">
        <Navbar />
        <main className="addStudentPage__main container-fluid">
          <div className="addStudentPage__content">
            <div className="addStudentPage__errorState">
              <span className="addStudentPage__errorIcon">⚠️</span>
              <h3 className="addStudentPage__errorTitle">
                Something went wrong
              </h3>
              <p className="addStudentPage__errorMessage">
                We couldn't load your data. Please check your connection and try
                again.
              </p>
              <button
                type="button"
                className="addStudentPage__retryBtn"
                onClick={refetchData}
              >
                🔄 Retry
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Pre-select the kid who already has transferred progress (if any)
  const preSelectedIndex = initialKids.findIndex(
    (kid) => kid.has_transferred_progress === true,
  );

  // Progress is locked when it's already assigned to an existing student
  const isProgressLocked = initialKids.some(
    (kid) => kid.has_transferred_progress === true,
  );

  const formInitialValues = {
    kids: initialKids,
    progressAssignedTo:
      preSelectedIndex !== -1
        ? preSelectedIndex
        : initialProgressAssignedTo,
  };

  return (
    <div className="addStudentPage">
      <Navbar />
      <main className="addStudentPage__main container-fluid">
        <div className="addStudentPage__content">
          <UnlockBenefits />

          <Formik
            initialValues={formInitialValues}
            validationSchema={validationSchema}
            onSubmit={() => {}}
            enableReinitialize
          >
            {({ values, isSubmitting, setFieldValue, setFieldError }) => {
              const canAddMore = values.kids.length < availableSlots;
              const remainingSlots = availableSlots - values.kids.length;
              const totalPrice = values.kids.reduce(
                (sum, _, idx) => sum + getPriceForKid(idx),
                0,
              );

              // Apply field-level API errors to Formik fields
              if (fieldErrors) {
                // Use queueMicrotask to avoid setting state during render
                queueMicrotask(() => {
                  const errorKeys = [];
                  Object.entries(fieldErrors).forEach(([key, messages]) => {
                    if (Array.isArray(messages) && messages.length > 0) {
                      setFieldError(key, messages[0]);
                      errorKeys.push(key);
                    }
                  });
                  // Clear after applying so they don't re-apply on every render
                  setFieldErrors(null);

                  // Scroll to the first error field (topmost in the DOM)
                  if (errorKeys.length > 0) {
                    // Allow Formik to re-render with errors before scrolling
                    requestAnimationFrame(() => {
                      // Find all error field elements and pick the one closest to the top
                      let firstErrorEl = null;
                      let firstErrorTop = Infinity;

                      for (const key of errorKeys) {
                        const el = document.querySelector(`[name="${key}"]`);
                        if (el) {
                          const rect = el.getBoundingClientRect();
                          if (rect.top < firstErrorTop) {
                            firstErrorTop = rect.top;
                            firstErrorEl = el;
                          }
                        }
                      }

                      if (firstErrorEl) {
                        firstErrorEl.scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });
                        firstErrorEl.focus({ preventScroll: true });
                      }
                    });
                  }
                });
              }

              const isFormDisabled = isSubmitting || isSubmittingApi;
              return (
                <Form>
                  <FormikDraftAutoSave parentId={parentId || getParentId()} />
                  <div className="addStudentPage__forms">
                    <FieldArray name="kids">
                      {({ push, remove }) => (
                        <>
                          {values.kids.map((kid, index) => (
                            <KidDetailsForm
                              key={kid._id || `new-${index}`}
                              index={index}
                              price={getPriceForKid(index)}
                              currencySymbol={currencySymbol}
                              billingPeriod={billingPeriod}
                              showRemove={values.kids.length > 1}
                              canRemove={
                                !(
                                  kid._status === "old" &&
                                  kid.has_transferred_progress
                                )
                              }
                              kidAge={kid.age}
                              status={kid._status}
                              isDeleting={deletingKidId === kid._id}
                              disabled={isFormDisabled}
                              onRemove={() =>
                                handleRemoveKid(
                                  kid,
                                  index,
                                  remove,
                                  values,
                                  setFieldValue,
                                )
                              }
                            />
                          ))}

                          <AddKidButton
                            price={getNextKidPrice(values.kids.length)}
                            currencySymbol={currencySymbol}
                            billingPeriod={billingPeriod}
                            disabled={!canAddMore || isFormDisabled}
                            remainingSlots={remainingSlots}
                            onAdd={() => push({ ...INITIAL_KID_STATE })}
                          />
                        </>
                      )}
                    </FieldArray>
                  </div>

                  <AssignProgress
                    isLocked={isProgressLocked || isFormDisabled}
                  />

                  {deleteError && (
                    <div
                      className="addStudentPage__api-error"
                      style={{
                        color: "red",
                        margin: "15px 0",
                        textAlign: "center",
                      }}
                    >
                      {deleteError}
                    </div>
                  )}
                  {error && (
                    <div
                      className="addStudentPage__api-error"
                      style={{
                        color: "red",
                        margin: "15px 0",
                        textAlign: "center",
                      }}
                    >
                      {error}
                    </div>
                  )}

                  <OnboardingPaymentButtons
                    currency={currency}
                    currencySymbol={currencySymbol}
                    totalPrice={totalPrice}
                    plan={plan}
                    billingPeriod={billingPeriod}
                    onSaveKids={handleSaveKids}
                    isDisabled={isFormDisabled}
                  />
                </Form>
              );
            }}
          </Formik>
        </div>
      </main>
    </div>
  );
};
